import { z } from 'zod';
import {
  supplierDocumentIngestionStatusSchema,
  supplierDocumentKindSchema,
  supplierDocumentLineSchema,
  supplierPurchaseOrderReferenceSchema,
} from './supplier-integration';

const externalIdSchema = z.string().trim().min(1).max(255);
const revisionSchema = z.string().trim().min(1).max(128);
const maxPurchaseOrderNumeric = 99_999_999.9999;

/**
 * Public machine-ingress contract. Tenant, company, account, source, document
 * kind and external supplier identity are deliberately absent: the server
 * derives all authority-bearing fields from the verified API key and DB.
 */
export const posPurchaseOrderApiImportLineSchema = supplierDocumentLineSchema.extend({
  external_line_id: externalIdSchema,
  supplier_sku: externalIdSchema,
  // Machine JSON must carry real numeric primitives. z.coerce would otherwise
  // silently reinterpret null/boolean/empty-string values as VAT/quantities.
  quantity: z.number().finite().positive().max(maxPurchaseOrderNumeric),
  unit_price: z.number().finite().nonnegative().max(maxPurchaseOrderNumeric).nullable().optional(),
  discount_percent: z.number().finite().min(0).max(100).optional(),
  vat_rate: z.number().finite().min(0).max(100),
}).superRefine((line, context) => {
  // Canonical PO lines currently have no lot persistence. Reject rather than
  // acknowledge and silently discard supplier lot provenance.
  if (line.lot_number !== undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['lot_number'],
      message: 'lot_number non è supportato per gli ordini di acquisto API',
    });
  }
});

export const posPurchaseOrderApiImportBodySchema = z.object({
  supplier_id: z.string().uuid(),
  warehouse_id: z.string().uuid(),
  external_document_id: externalIdSchema,
  revision: revisionSchema,
  document_number: z.string().trim().min(1).max(255).optional(),
  document_date: z.string().date(),
  lines: z.array(posPurchaseOrderApiImportLineSchema).min(1).max(10_000),
}).strict();
export type PosPurchaseOrderApiImportBody = z.infer<
  typeof posPurchaseOrderApiImportBodySchema
>;

export const supplierDocumentIngestionResultSchema = z.object({
  status: supplierDocumentIngestionStatusSchema,
  kind: supplierDocumentKindSchema,
  external_document_id: z.string().nullable(),
  internal_document_id: z.string(),
  payload_hash: z.string().regex(/^[0-9a-f]{64}$/i),
  revision: z.string().nullable(),
  unresolved_purchase_order_reference: supplierPurchaseOrderReferenceSchema.nullable(),
  internal_line_ids: z.array(z.string()),
}).strict();

export const posPurchaseOrderApiImportResponseSchema = z.object({
  success: z.literal(true),
  data: supplierDocumentIngestionResultSchema,
}).strict();
export type PosPurchaseOrderApiImportResponse = z.infer<
  typeof posPurchaseOrderApiImportResponseSchema
>;

export const posPurchaseOrderApiImportListItemSchema = z.object({
  id: z.string(),
  order_number: z.string(),
  supplier_name: z.string(),
  warehouse_name: z.string(),
  external_document_id: z.string(),
  document_number: z.string().nullable(),
  revision: z.string(),
  received_at: z.string(),
}).strict();

/** Browser route envelope; callers consume the list payload at response.data. */
export const posPurchaseOrderApiImportListResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    items: z.array(posPurchaseOrderApiImportListItemSchema),
    total: z.number().int().nonnegative(),
  }).strict(),
  meta: z.object({
    timestamp: z.union([z.string(), z.date()]),
    request_id: z.string(),
    tenant_id: z.string().optional(),
  }).passthrough(),
}).passthrough();
export type PosPurchaseOrderApiImportListResponse = z.infer<
  typeof posPurchaseOrderApiImportListResponseSchema
>;